import type { HTMLMotionProps } from 'motion/react'
import { motion } from 'motion/react'

import type { BookDetails } from '@package/api'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ui/web/components'
import { BookRowPremiumBadge } from '@ui/web/icon'
import { getInitials } from '@helper/utils/format'
import { useBreakpoint } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

interface BookModalProps extends Omit<HTMLMotionProps<'button'>, '$typeof'> {
  data: BookDetails
  className?: string
  size?: number
  children?: React.ReactNode
  withAnimation?: boolean
}

export function BookModal({ data, className, size = 200, children, withAnimation = true, ...props }: BookModalProps) {
  const isMd = useBreakpoint('md', 'min')

  return (
    <Dialog>
      <DialogTrigger
        render={
          <motion.button
            initial={withAnimation ? { opacity: 0, x: 10 } : undefined}
            whileInView={withAnimation ? { opacity: 1, x: 0 } : undefined}
            transition={withAnimation ? { duration: 0.5, delay: 0.1 } : undefined}
            className={cn('srd:w-fit srd:h-fit srd:relative', className)}
            type='button'
            {...props}
          >
            {data.coverUrl && (
              <img
                src={data.coverUrl}
                alt={data.title}
                width={size}
                height={size}
                className='srd:w-full srd:object-cover srd:rounded-md srd:aspect-3/4'
                draggable={false}
              />
            )}

            {!data.isFree && (
              <div className='srd:absolute srd:bottom-0 srd:right-0 srd:rounded-br-md srd:overflow-clip'>
                <BookRowPremiumBadge />
              </div>
            )}
          </motion.button>
        }
      />
      <DialogContent className='srd:w-full srd:max-w-[1000px] srd:h-fit srd:max-h-[90dvh] srd:overflow-y-auto srd:flex'>
        <DialogTitle className='srd:sr-only'>{data.title}</DialogTitle>
        <DialogDescription className='srd:sr-only'>{data.synopsis}</DialogDescription>

        <div className='srd:w-full srd:h-fit srd:flex srd:flex-col srd:gap-y-8'>
          <section className='srd:grid srd:md:grid-cols-2 srd:grid-cols-1 srd:gap-x-4 srd:gap-y-8'>
            <div
              className={cn('srd:w-full srd:flex srd:items-center', isMd ? 'srd:justify-start' : 'srd:justify-center')}
            >
              <div className='srd:w-fit srd:h-fit srd:rounded-md srd:aspect-3/4 srd:overflow-clip srd:relative'>
                {isMd ? (
                  <img
                    src={data.coverUrl}
                    alt={data.title}
                    width={400}
                    height={400}
                    className='srd:object-contain srd:rounded-md srd:aspect-3/4'
                    draggable={false}
                  />
                ) : (
                  <img
                    src={data.coverUrl}
                    alt={data.title}
                    width={200}
                    height={200}
                    className='srd:object-contain srd:rounded-md srd:aspect-3/4'
                    draggable={false}
                  />
                )}

                {!data.isFree && (
                  <div className='srd:absolute srd:bottom-0 srd:right-0 srd:rounded-br-md srd:overflow-clip'>
                    <BookRowPremiumBadge />
                  </div>
                )}
              </div>
            </div>

            <section className='srd:flex srd:flex-col srd:gap-y-4'>
              <h6 className='srd:md:text-2xl srd:text-xl srd:font-bold'>{data.title}</h6>

              <p>{data.synopsis}</p>

              <section className='srd:flex srd:flex-col srd:gap-y-0'>
                <p>Pengarang:</p>
                <p className='srd:font-semibold'>
                  {data.authors.map((v, index, array) => {
                    const name = v.bio ? (
                      <Tooltip key={`${v.name}-${index}-author`}>
                        <TooltipTrigger className='srd:w-fit srd:h-fit srd:font-semibold' type='button'>
                          {v.name}
                        </TooltipTrigger>
                        <TooltipContent className='srd:text-xs srd:max-w-[200px] srd:text-left'>
                          <p>{v.bio}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      v.name
                    )

                    if (index === array.length - 1) {
                      return name
                    } else if (index === array.length - 2 && array.length > 1) {
                      return <>{name}, dan </>
                    } else {
                      return <>{name}, </>
                    }
                  })}
                </p>
              </section>

              <section className='srd:flex srd:flex-col srd:gap-y-0'>
                <p>Illustrator:</p>
                <p className='srd:font-semibold'>
                  {data.illustrators.map((v, index, array) => {
                    const name = v.bio ? (
                      <Tooltip key={`${v.name}-${index}-illustrator`}>
                        <TooltipTrigger className='srd:w-fit srd:h-fit srd:font-semibold' type='button'>
                          {v.name}
                        </TooltipTrigger>
                        <TooltipContent className='srd:text-xs srd:max-w-[200px] srd:text-left'>
                          <p>{v.bio}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      v.name
                    )

                    if (index === array.length - 1) {
                      return name
                    } else if (index === array.length - 2 && array.length > 1) {
                      return <>{name}, dan </>
                    } else {
                      return <>{name}, </>
                    }
                  })}
                </p>
              </section>

              <section className='srd:flex srd:flex-col srd:gap-y-0'>
                <p>Penerbit:</p>
                <p className='srd:font-semibold srd:flex srd:items-center'>
                  <Avatar profile='organization' className='srd:size-5 srd:shrink-0 srd:mr-1'>
                    <AvatarImage src={data.publisher.logo ?? undefined} alt={data.publisher.name} />
                    <AvatarFallback>{getInitials(data.publisher.name)}</AvatarFallback>
                  </Avatar>

                  {data.publisher.name}
                </p>
              </section>

              {data.bookPublishedOn && (
                <section className='srd:flex srd:flex-col srd:gap-y-0'>
                  <p>Tahun:</p>
                  <p className='srd:font-semibold'>{new Date(data.bookPublishedOn).getFullYear()}</p>
                </section>
              )}

              <section className='srd:flex srd:flex-col srd:gap-y-0'>
                <p>Usia:</p>
                <p className='srd:font-semibold'>{`${data.minAge} - ${data.maxAge} Tahun`}</p>
              </section>

              <section className='srd:flex srd:flex-col srd:gap-y-0'>
                <p>Jenjang Baca:</p>
                {data.level.description ? (
                  <Tooltip>
                    <TooltipTrigger
                      className='srd:w-fit srd:h-fit srd:font-semibold srd:flex srd:items-center'
                      type='button'
                    >
                      {data.level.name}
                    </TooltipTrigger>
                    <TooltipContent className='srd:text-xs srd:max-w-[200px] srd:text-left'>
                      <p>{data.level.description}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <p className='srd:font-semibold srd:flex srd:items-center'>{data.level.name}</p>
                )}
              </section>

              <section className='srd:flex srd:flex-col srd:gap-y-0'>
                <p>Tema:</p>

                <div className='srd:flex srd:flex-wrap srd:gap-x-4 srd:gap-y-2'>
                  {data.bookToBookThemes.map((v) => {
                    if (v.bookTheme.description) {
                      return (
                        <Tooltip>
                          <TooltipTrigger
                            className='srd:w-fit srd:h-fit srd:font-semibold srd:flex srd:items-center'
                            type='button'
                          >
                            <img
                              src={v.bookTheme.iconUrl}
                              alt={v.bookTheme.name}
                              width={20}
                              height={20}
                              className='srd:size-5 srd:rounded-full srd:shrink-0 srd:mr-2'
                              draggable={false}
                            />
                            {v.bookTheme.name}
                          </TooltipTrigger>
                          <TooltipContent className='srd:text-xs srd:max-w-[200px] srd:text-left'>
                            <p>{v.bookTheme.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      )
                    }

                    return (
                      <p className='srd:font-semibold srd:flex srd:items-center'>
                        <img
                          src={v.bookTheme.iconUrl}
                          alt={v.bookTheme.name}
                          width={20}
                          height={20}
                          className='srd:size-5 srd:rounded-full srd:shrink-0 srd:mr-2'
                          draggable={false}
                        />
                        {v.bookTheme.name}
                      </p>
                    )
                  })}
                </div>
              </section>
            </section>
          </section>

          <section className='srd:w-full srd:flex srd:flex-col srd:gap-y-4'>{children}</section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
